import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Lock, LifeBuoy, ChevronRight, Sun, Moon, Monitor, MessageCircleQuestion, Bug } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { saveTheme } from "@/lib/theme";
import { SkeletonList } from "@/components/Mascot";
import { Switch } from "@/components/ui/switch";
import { useReauth } from "@/components/account/ConfirmIdentity";
import { ProfileSection, Section } from "@/components/account/ProfileSection";
import { SecuritySection } from "@/components/account/SecuritySection";
import { DataSection } from "@/components/account/DataSection";
import { SupportDialog } from "@/components/account/SupportDialog";

function Page({ title, testid, children }) {
  return (
    <div className="flex-1 overflow-y-auto scroll-thin">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid={testid}>
        <h1 className="font-display text-4xl font-semibold tracking-tight">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function useAccount() {
  const [account, setAccount] = useState(null);
  const load = useCallback(() => http.get("/account").then((r) => setAccount(r.data)).catch((e) => toast.error(formatError(e))), []);
  useEffect(() => { load(); }, [load]);
  return { account, setAccount, load };
}

export function AccountPage() {
  const { logout } = useAuth();
  const { account, load } = useAccount();
  const { getToken, dialog } = useReauth(account);
  if (!account) return <Page title="Account" testid="account-page"><SkeletonList rows={3} /></Page>;
  const props = { account, getToken, onChanged: load };
  return (
    <Page title="Account" testid="account-page">
      <ProfileSection {...props} />
      <SecuritySection {...props} />
      <DataSection {...props} />
      <button onClick={logout} data-testid="account-logout-btn" className="h-11 px-5 rounded-full border border-white/15 text-sm hover:bg-white/5 inline-flex items-center gap-2"><LogOut className="h-4 w-4" />Sign out</button>
      {dialog}
    </Page>
  );
}

const THEMES = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
  { id: "system", label: "System", icon: Monitor },
];

function Appearance() {
  const { user, setUser } = useAuth();
  const current = user?.theme || "dark";
  return (
    <Section title="Appearance" text="System follows your device's light or dark setting. The public website always stays dark." testid="settings-appearance">
      <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Theme">
        {THEMES.map((t) => (
          <button key={t.id} role="radio" aria-checked={current === t.id} onClick={() => saveTheme(t.id, setUser)} data-testid={`theme-${t.id}`}
            className={`h-20 rounded-2xl border flex flex-col items-center justify-center gap-1.5 text-sm transition-colors ${current === t.id ? "border-sky-400 bg-sky-400/10 text-sky-300" : "border-white/10 text-neutral-400 hover:bg-white/[0.04]"}`}>
            <t.icon className="h-5 w-5" />{t.label}
          </button>
        ))}
      </div>
    </Section>
  );
}

const NOTIFY = [
  { key: "group_activity", title: "Group activity", text: "An email when someone shares notes or files in one of your groups (at most every few hours per group)." },
  { key: "product_updates", title: "Product updates", text: "Occasional news about new BrynAI features." },
  { key: "offers", title: "Tips and offers", text: "Study tips and plan offers now and then." },
];

function Notifications() {
  const { account, setAccount } = useAccount();
  const set = async (key, value) => {
    setAccount((a) => ({ ...a, notify: { ...a.notify, [key]: value } }));
    try { const { data } = await http.put("/account/prefs", { notify: { [key]: value } }); setAccount(data); }
    catch (e) { toast.error(formatError(e)); setAccount((a) => ({ ...a, notify: { ...a.notify, [key]: !value } })); }
  };
  return (
    <Section title="Email notifications" text="Security emails, such as sign-in codes, are always sent." testid="settings-notifications">
      {!account ? <SkeletonList rows={2} /> : NOTIFY.map((n) => (
        <div key={n.key} className="flex items-start gap-4">
          <div className="flex-1 min-w-0"><p className="text-sm text-neutral-100">{n.title}</p><p className="text-xs text-neutral-500">{n.text}</p></div>
          <Switch checked={!!account.notify[n.key]} onCheckedChange={(v) => set(n.key, v)} aria-label={n.title} data-testid={`notify-${n.key}-switch`} />
        </div>
      ))}
    </Section>
  );
}

const LinkRow = ({ to, icon: Icon, title, text, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-white/[0.03] transition-colors">
    <Icon className="h-5 w-5 text-sky-300 shrink-0" />
    <span className="flex-1 min-w-0"><span className="block text-sm text-neutral-100">{title}</span><span className="block text-xs text-neutral-500">{text}</span></span>
    <ChevronRight className="h-4 w-4 text-neutral-600" />
  </Link>
);

const ActionRow = ({ icon: Icon, title, text, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-full text-left flex items-center gap-3 -mx-2 px-2 py-3 rounded-xl hover:bg-white/[0.03] transition-colors">
    <Icon className="h-5 w-5 text-sky-300 shrink-0" />
    <span className="flex-1 min-w-0"><span className="block text-sm text-neutral-100">{title}</span><span className="block text-xs text-neutral-500">{text}</span></span>
    <ChevronRight className="h-4 w-4 text-neutral-600" />
  </button>
);

export function SettingsPage() {
  const [support, setSupport] = useState(null);
  return (
    <Page title="Settings" testid="settings-page">
      <Appearance />
      <Notifications />
      <Section title="Privacy and help" testid="settings-help-section">
        <div className="-my-2">
          <LinkRow to="/app/memory" icon={Lock} title="What I know about you" text="See and delete the private notes Bryn keeps to help you" testid="settings-private-notes" />
          <LinkRow to="/app/help" icon={LifeBuoy} title="Help center" text="Guides to how BrynAI works" testid="settings-help" />
          <ActionRow icon={MessageCircleQuestion} title="Contact support" text="Questions about your account, plan or anything else" onClick={() => setSupport("support")} testid="settings-contact-support" />
          <ActionRow icon={Bug} title="Report a problem" text="Something broken or not working as expected" onClick={() => setSupport("problem")} testid="settings-report-problem" />
        </div>
      </Section>
      {support && <SupportDialog kind={support} onClose={() => setSupport(null)} />}
    </Page>
  );
}
